import React, { Dispatch, FC, ReactElement, SetStateAction, useEffect, useRef } from 'react'
import { TrophyMemoryDto } from '~/modules/Trophies/Infrastructure/TrophyDetailComponentDto'
import styles from './TrophyMemoryLightbox.module.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CSSTransition } from 'react-transition-group'
import TailwindConfig from '~/tailwind.config'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'next-i18next'

interface Props {
  memory: TrophyMemoryDto
  active: boolean
  setActive: Dispatch<SetStateAction<boolean>>
  appearance?: 'default' | 'market'
}

export const TrophyMemoryLightbox: FC<Props> = ({ memory, active, setActive, appearance = 'default' }) => {
  const { t } = useTranslation('trophy_details')
  let media: ReactElement
  const overlayRef = useRef<HTMLDivElement>(null)
  const videoPlayer = useRef<HTMLVideoElement>(null)
  const audioPlayer = useRef<HTMLAudioElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const previousActiveElementRef = useRef<HTMLElement | null>(null)

  const closeLightbox = () => {
    setActive(false)
    audioPlayer.current?.pause()
    videoPlayer.current?.pause()
  }

  useEffect(() => {
    if (!active) {
      return
    }

    previousActiveElementRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null

    closeButtonRef.current?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActive(false)
        audioPlayer.current?.pause()
        videoPlayer.current?.pause()
      }
    }

    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      previousActiveElementRef.current?.focus()
    }
  }, [active, setActive])

  switch (memory.type) {
    case 'video':
      media = (
        <video
          data-qa='trophy-memories-lightbox-video'
          className={ styles.trophyMemoryLightbox__trophyMemoriesLightboxVideo }
          poster={ memory.thumbnailUrl }
          loop={ true }
          autoPlay={ false }
          muted={ false }
          controls
          ref={ videoPlayer }
          onClick={ (event) => event.stopPropagation() }
        >
          <source
            src={ memory.mediaUrl }
            type={ 'video/mp4' }
          />
        </video>
      )
      break

    case 'image':
      media = (
        <img
          data-qa='trophy-memories-lightbox-image'
          className={ styles.trophyMemoryLightbox__trophyMemoriesLightboxImage }
          alt={ '' }
          src={ memory.mediaUrl }
          width={ 1280 }
          height={ 720 }
        />
      )
      break

    case 'audio':
      media = (
        <audio
          data-qa='trophy-memories-lightbox-audio'
          className={ styles.trophyMemoryLightbox__trophyMemoriesLightboxAudio }
          src={ memory.mediaUrl }
          controls
          ref={ audioPlayer }
          onClick={ (event) => event.stopPropagation() }
        />
      )
      break
  }

  return (
    <CSSTransition
      classNames={ {
        enter: styles.trophyMemoryLightbox__trophyMemoriesLightboxEnter,
        enterActive: styles.trophyMemoryLightbox__trophyMemoriesLightboxEnterActive,
        enterDone: styles.trophyMemoryLightbox__trophyMemoriesLightboxEnterDone,
        exit: styles.trophyMemoryLightbox__trophyMemoriesLightboxExit,
        exitActive: styles.trophyMemoryLightbox__trophyMemoriesLightboxExitActive,
        exitDone: styles.trophyMemoryLightbox__trophyMemoriesLightboxExitDone,
      } }
      in={ active }
      nodeRef={ overlayRef }
      timeout={ parseInt(TailwindConfig.theme.extend.transitionDuration.DEFAULT) }
      unmountOnExit
    >
      <div
        data-qa='trophy-memories-lightbox'
        className={ `
          ${styles.trophyMemoryLightbox__trophyMemoriesLightbox}
          ${appearance === 'market' ? styles.trophyMemoryLightbox__market : ''}
        ` }
        onClick={ closeLightbox }
        ref={ overlayRef }
        role="dialog"
        aria-modal="true"
        aria-label={ t('memory_lightbox_dialog_label') }
      >
        <div
          className={ styles.trophyMemoryLightbox__mediaContainer }
          onClick={ (event) => event.stopPropagation() }
        >
          { media }

          <button
            type="button"
            className={ styles.trophyMemoryLightbox__closeButton }
            onClick={ closeLightbox }
            aria-label={ t('memory_lightbox_close_label') }
            ref={ closeButtonRef }
          >
            <FontAwesomeIcon
              icon={ faXmark }
              className={ styles.trophyMemoryLightbox__trophyMemoriesLightboxIcon }
              aria-hidden={ true }
            />
          </button>
        </div>

      </div>

    </CSSTransition>
  )
}
